import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { borderRadius, spacing } from '@tabbio/tokens';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import {
  ProfileGroupCard,
  ProfileSectionHeader,
  ProfileSheetScaffold,
} from './ProfileSheetScaffold';
import {
  getPublicProfileTheme,
  PUBLIC_PROFILE_ACCENTS,
  PUBLIC_PROFILE_THEMES,
} from '../../lib/public-profile-builder';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';

const DESIGN_TABS = ['themes', 'accents', 'buttons'] as const;

export function ProfileDesignSheet() {
  const { t } = useTranslation('common');
  const { theme: appTheme } = useTheme();
  const haptics = useHaptics();
  const router = useRouter();
  const {
    theme,
    setTheme,
    accent,
    setAccent,
    buttonStyle,
    setButtonStyle,
    buttonShape,
    setButtonShape,
    draftProfile,
    username,
    routeMode,
    setIsPreview,
  } = usePublicProfileBuilderModel();

  const [tab, setTab] = useState<(typeof DESIGN_TABS)[number]>('themes');
  const selectedTheme = useMemo(() => getPublicProfileTheme(theme), [theme]);
  const profileRoutes = useMemo(() => getPublicProfileRoutes(routeMode), [routeMode]);

  const buttonRadius =
    buttonShape === 'pill' ? 999 : buttonShape === 'square' ? borderRadius.sm : borderRadius.xl;
  const buttonBackground =
    buttonStyle === 'fill'
      ? accent
      : buttonStyle === 'soft'
        ? `${accent}24`
        : buttonStyle === 'glass'
          ? 'rgba(255,255,255,0.22)'
          : 'transparent';
  const buttonBorder = buttonStyle === 'outline' ? accent : 'rgba(255,255,255,0.32)';
  const buttonText = buttonStyle === 'fill' ? '#FFFFFF' : selectedTheme.previewText;
  const previewName = draftProfile.name.trim() || t('publicProfile.defaultName');
  const isLightPreview = selectedTheme.previewText === '#111827';

  const openPreview = () => {
    haptics.trigger('light');
    setIsPreview(true);
    router.replace(profileRoutes.index as never);
  };

  return (
    <ProfileSheetScaffold
      title={t('publicProfile.design.title')}
      subtitle={t('publicProfile.design.subtitle')}
      compact
    >
      <ProfileSectionHeader title={t('publicProfile.design.previewTitle')} />
      <LinearGradient
        colors={[selectedTheme.previewBg, selectedTheme.previewCard]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.previewCard}
      >
        <Text
          style={[
            styles.previewTitle,
            { color: isLightPreview ? '#334155' : 'rgba(255,255,255,0.78)' },
          ]}
        >
          {t('publicProfile.design.previewTitle')}
        </Text>
        <Text
          style={[styles.previewName, { color: selectedTheme.previewText }]}
          numberOfLines={1}
        >
          {previewName}
        </Text>
        <Text
          style={[
            styles.previewHandle,
            { color: isLightPreview ? '#475569' : 'rgba(255,255,255,0.72)' },
          ]}
        >
          @{username}
        </Text>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('publicProfile.preview')}
          onPress={openPreview}
          style={[
            styles.previewButton,
            {
              borderRadius: buttonRadius,
              backgroundColor: buttonBackground,
              borderColor: buttonBorder,
            },
          ]}
        >
          <Text style={[styles.previewButtonText, { color: buttonText }]}>
            {t('publicProfile.preview')}
          </Text>
        </Pressable>
      </LinearGradient>

      <View style={[styles.controlDock, { backgroundColor: appTheme.surface, borderColor: appTheme.borderSubtle }]}>
        {DESIGN_TABS.map((item) => {
          const active = tab === item;
          return (
            <Pressable
              key={item}
              onPress={() => setTab(item)}
              style={({ pressed }) => [
                styles.controlDockItem,
                {
                  opacity: pressed ? 0.78 : 1,
                  backgroundColor: active ? appTheme.text : 'transparent',
                },
              ]}
            >
              <Text
                style={[
                  styles.controlDockLabel,
                  { color: active ? appTheme.background : appTheme.text },
                ]}
              >
                {t(`publicProfile.design.tabs.${item}` as never)}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {tab === 'themes' ? (
        <>
          <ProfileSectionHeader title={t('publicProfile.design.tabs.themes')} />
          <View style={styles.themeGrid}>
            {PUBLIC_PROFILE_THEMES.map((option) => {
              const active = option.id === theme;
              return (
                <Pressable
                  key={option.id}
                  onPress={() => setTheme(option.id)}
                  style={({ pressed }) => [
                    styles.themeCard,
                    {
                      opacity: pressed ? 0.84 : 1,
                      borderColor: active ? accent : appTheme.borderSubtle,
                      backgroundColor: appTheme.surface,
                    },
                  ]}
                >
                  <LinearGradient
                    colors={[option.previewBg, option.previewCard]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.themeSwatch}
                  />
                  <Text style={[styles.themeName, { color: appTheme.text }]} numberOfLines={1}>
                    {t(option.labelKey as never)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      ) : null}

      {tab === 'accents' ? (
        <>
          <ProfileSectionHeader title={t('publicProfile.design.tabs.accents')} />
          <View style={styles.accentWrap}>
            {PUBLIC_PROFILE_ACCENTS.map((color) => {
              const active = color === accent;
              return (
                <Pressable
                  key={color}
                  onPress={() => setAccent(color)}
                  style={({ pressed }) => [
                    styles.accentDot,
                    {
                      opacity: pressed ? 0.75 : 1,
                      backgroundColor: color,
                      borderColor: active ? appTheme.text : appTheme.background,
                    },
                  ]}
                />
              );
            })}
          </View>
        </>
      ) : null}

      {tab === 'buttons' ? (
        <>
          <ProfileSectionHeader title={t('publicProfile.design.tabs.buttons')} />
          <ProfileGroupCard>
            <View style={styles.buttonSettingsWrap}>
              <Text style={[styles.settingsTitle, { color: appTheme.text }]}>
                {t('publicProfile.design.buttonStyle')}
              </Text>
              <View style={styles.inlineButtons}>
                {(['fill', 'outline', 'soft', 'glass'] as const).map((styleValue) => {
                  const active = styleValue === buttonStyle;
                  return (
                    <Pressable
                      key={styleValue}
                      onPress={() => setButtonStyle(styleValue)}
                      style={({ pressed }) => [
                        styles.inlineButton,
                        {
                          opacity: pressed ? 0.8 : 1,
                          backgroundColor: active ? appTheme.text : appTheme.background,
                          borderColor: appTheme.borderSubtle,
                        },
                      ]}
                    >
                      <Text
                        style={{
                          color: active ? appTheme.background : appTheme.text,
                          fontSize: 12,
                          fontWeight: '700',
                        }}
                      >
                        {t(`publicProfile.design.buttonStyles.${styleValue}` as never)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              <Text style={[styles.settingsTitle, { color: appTheme.text }]}>
                {t('publicProfile.design.buttonShape')}
              </Text>
              <View style={styles.inlineButtons}>
                {(['square', 'rounded', 'pill'] as const).map((shapeValue) => {
                  const active = shapeValue === buttonShape;
                  return (
                    <Pressable
                      key={shapeValue}
                      onPress={() => setButtonShape(shapeValue)}
                      style={({ pressed }) => [
                        styles.inlineButton,
                        {
                          opacity: pressed ? 0.8 : 1,
                          borderRadius: shapeValue === 'pill' ? 999 : shapeValue === 'rounded' ? 12 : 6,
                          backgroundColor: active ? appTheme.text : appTheme.background,
                          borderColor: appTheme.borderSubtle,
                        },
                      ]}
                    >
                      <Text
                        style={{
                          color: active ? appTheme.background : appTheme.text,
                          fontSize: 12,
                          fontWeight: '700',
                        }}
                      >
                        {t(`publicProfile.design.buttonShapes.${shapeValue}` as never)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </ProfileGroupCard>
        </>
      ) : null}
    </ProfileSheetScaffold>
  );
}

const styles = StyleSheet.create({
  previewCard: {
    borderRadius: 16,
    paddingVertical: spacing[4],
    paddingHorizontal: spacing[3],
    alignItems: 'center',
    gap: 6,
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  previewName: {
    fontSize: 18,
    fontWeight: '700',
  },
  previewHandle: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 4,
  },
  previewButton: {
    borderWidth: 1,
    minWidth: 134,
    minHeight: 38,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  previewButtonText: {
    fontSize: 13,
    fontWeight: '700',
  },
  controlDock: {
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    minHeight: 44,
    padding: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  controlDockItem: {
    flex: 1,
    minHeight: 34,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlDockLabel: {
    fontSize: 12,
    fontWeight: '700',
  },
  themeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  themeCard: {
    width: '31%',
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 8,
    gap: 8,
  },
  themeSwatch: {
    height: 46,
    borderRadius: 8,
  },
  themeName: {
    fontSize: 12,
    fontWeight: '600',
  },
  accentWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'center',
    paddingVertical: spacing[2],
  },
  accentDot: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 3,
  },
  buttonSettingsWrap: {
    gap: spacing[2],
    padding: spacing[3],
  },
  settingsTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  inlineButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  inlineButton: {
    minWidth: 72,
    height: 34,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
});
